// File: client/src/components/dashboard/ContaDashboard.js
import React, { useState, useMemo, useEffect } from 'react';
import apiClient from '../../services/api';

// ========================================================================
// <<< CORREÇÃO: USANDO NOVO HOOK DE RELATÓRIOS >>>
// ========================================================================
import useAlert from '../../hooks/useAlert';
import { useAuth } from '../../context/auth/AuthProvider';
import { useContaDashboard } from '../../hooks/useReports';
import { useTenantI18n } from '../../hooks/useAccounts';
import { formatCurrency } from '../../utils/formatters';
import PageHeader from '../layout/PageHeader';
import KpiCard from './KpiCard';
import BarChart from './BarChart';
import DoughnutChart from './DoughnutChart';
import { getAndRegisterDatePickerLocale } from '../../utils/dateLocale';
// ========================================================================

import { LayoutDashboard, DollarSign, BarChart2, TrendingUp, Users, Filter as FilterIcon } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const ContaDashboard = () => {
    const { setAlert } = useAlert();
    const { user } = useAuth();
    
    // 🔥 ESTADO DE FILTROS
    const [filters, setFilters] = useState({
        startDate: new Date(new Date().setDate(1)),
        endDate: new Date(),
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        unidadeId: '',
        profissionalId: '',
    });

    const [filterOptions, setFilterOptions] = useState({
        unidades: [],
        profissionais: []
    });
    
    const [showFilters, setShowFilters] = useState(false);

    // 🔥 USANDO NOVO HOOK - useContaDashboard
    const { 
        data: dashboardData, 
        isLoading 
    } = useContaDashboard({
        startDate: filters.startDate,
        endDate: filters.endDate,
        year: filters.year,
        month: filters.month,
        unidadeId: filters.unidadeId,
        profissionalId: filters.profissionalId
    });

    // 🔥 CARREGAR OPÇÕES DE FILTRO - CORRIGIDO
    useEffect(() => {
        const fetchFilterOptions = async () => {
            // ✅ CORREÇÃO: só bloqueava sem user.account — franquia_admin
            // não tem `.account` (o tenant dele é `.franquia`) e ficava
            // sem nenhuma opção de filtro. O backend (filterByUserUnits)
            // já sabe escopar Unidade/User por franquia sozinho quando
            // nenhum `account` é enviado — só precisa que a chamada saia.
            if (!user?.account?._id && !user?.franquia?._id) return;

            try {
                const baseParams = { limit: 1000 };
                if (user?.account?._id) baseParams.account = user.account._id;

                // ✅ CORREÇÃO: Removido prefixo /api/v1
                const [unidadesRes, profissionaisRes] = await Promise.all([
                    apiClient.get('/unidades', { params: baseParams }),
                    apiClient.get('/users', { params: { ...baseParams, role: 'profissional' } })
                ]);
                
                setFilterOptions({
                    unidades: unidadesRes.data?.data || [],
                    profissionais: profissionaisRes.data?.data || [],
                });
                
                console.log('✅ Filtros carregados:', {
                    unidades: unidadesRes.data?.data?.length || 0,
                    profissionais: profissionaisRes.data?.data?.length || 0
                });
            } catch (error) {
                console.error('❌ Erro ao carregar filtros:', error);
                setAlert('Erro ao carregar opções de filtro.', 'danger');
            }
        };
        
        fetchFilterOptions();
    }, [user, setAlert]);

    // 🔥 MANIPULADOR DE MUDANÇAS DE FILTRO
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    // 🔥 OPÇÕES PARA SELECTS
    const yearOptions = useMemo(() => 
        Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i), 
    []);

    const monthOptions = useMemo(() => [
        { value: 1, label: 'Janeiro' }, { value: 2, label: 'Fevereiro' }, 
        { value: 3, label: 'Março' }, { value: 4, label: 'Abril' }, 
        { value: 5, label: 'Maio' }, { value: 6, label: 'Junho' },
        { value: 7, label: 'Julho' }, { value: 8, label: 'Agosto' }, 
        { value: 9, label: 'Setembro' }, { value: 10, label: 'Outubro' }, 
        { value: 11, label: 'Novembro' }, { value: 12, label: 'Dezembro' }
    ], []);

    // 🔥 PREPARAÇÃO DOS DADOS PARA GRÁFICOS
    const dailyChartData = useMemo(() => {
        const daysInMonth = new Date(filters.year, filters.month, 0).getDate();
        const labels = Array.from({ length: daysInMonth }, (_, i) => i + 1);
        const chartData = Array(daysInMonth).fill(0);
        
        dashboardData?.evolucaoDiaria?.forEach(item => {
            if (item._id >= 1 && item._id <= daysInMonth) {
                chartData[item._id - 1] = item.count;
            }
        });
        
        return {
            labels,
            datasets: [{ 
                label: `Agendamentos em ${monthOptions.find(m => m.value === filters.month)?.label}`, 
                data: chartData, 
                backgroundColor: 'rgba(54, 162, 235, 0.7)' 
            }]
        };
    }, [dashboardData?.evolucaoDiaria, filters.year, filters.month, monthOptions]);

    const monthlyChartData = useMemo(() => {
        const labels = monthOptions.map(m => m.label.substring(0, 3));
        const chartData = Array(12).fill(0);
        
        dashboardData?.evolucaoMensal?.forEach(item => {
            if (item._id >= 1 && item._id <= 12) {
                chartData[item._id - 1] = item.count;
            }
        });
        
        return {
            labels,
            datasets: [{ 
                label: `Agendamentos em ${filters.year}`, 
                data: chartData, 
                backgroundColor: 'rgba(75, 192, 192, 0.7)' 
            }]
        };
    }, [dashboardData?.evolucaoMensal, filters.year, monthOptions]);
    
    const annualChartData = useMemo(() => {
        const labels = dashboardData?.evolucaoAnual?.map(item => item._id) || [];
        const chartData = dashboardData?.evolucaoAnual?.map(item => item.count) || [];
        
        return {
            labels,
            datasets: [{ 
                label: 'Total de Agendamentos por Ano', 
                data: chartData, 
                backgroundColor: 'rgba(153, 102, 255, 0.7)' 
            }]
        };
    }, [dashboardData?.evolucaoAnual]);

    // 🔥 CONFIGURAÇÕES DE LOCALIDADE
    // ✅ ACHADO CRÍTICO: lia configuracoesGerais.locale, mas o campo real
    // fica em configuracoesGerais.i18n.locale — sempre undefined, sempre
    // caindo no default 'pt-BR'. Trocado pro hook useTenantI18n(), que já
    // resolve certo pra conta própria OU conta selecionada por
    // matriz/franquia (mesmo padrão de accountId usado em toda chamada de
    // API do app).
    const { locale: contaLocale, currency: contaCurrency } = useTenantI18n();
    const datePickerLocale = getAndRegisterDatePickerLocale(contaLocale);

    const chartOptions = {
        scales: { 
            y: { 
                beginAtZero: true, 
                ticks: { stepSize: 1 } 
            } 
        }
    };

    // 🔥 AÇÕES DA PÁGINA
    const pageActions = (
        <button 
            onClick={() => setShowFilters(prevState => !prevState)} 
            className="action-button action-button-secondary"
        >
            <FilterIcon size={18} className="icon"/> 
            {showFilters ? 'Ocultar Filtros' : 'Exibir Filtros'}
        </button>
    );

    return (
        <div className="page-management-container">
            <PageHeader 
                icon={<LayoutDashboard size={24} />} 
                title="Dashboard Operacional"
                actions={pageActions}
            />

            {/* 🔥 SEÇÃO DE FILTROS */}
            {showFilters && (
                <div className="content-panel">
                    <h3 className="panel-title">Filtros Gerais</h3>
                    <div className="form-grid-container">
                        <div className="form-group">
                            <label>Unidade</label>
                            <select 
                                className="form-input" 
                                value={filters.unidadeId} 
                                onChange={e => handleFilterChange('unidadeId', e.target.value)}
                            >
                                <option value="">Todas</option>
                                {filterOptions.unidades.map(unidade => (
                                    <option key={unidade._id} value={unidade._id}>{unidade.nome}</option>
                                ))}
                            </select>
                        </div>
                        
                        <div className="form-group">
                            <label>Profissional</label>
                            <select 
                                className="form-input" 
                                value={filters.profissionalId} 
                                onChange={e => handleFilterChange('profissionalId', e.target.value)}
                            >
                                <option value="">Todos</option>
                                {filterOptions.profissionais.map(profissional => (
                                    <option key={profissional._id} value={profissional._id}>{profissional.name}</option>
                                ))}
                            </select>
                        </div>
                        
                        <div className="form-group">
                            <label>Ano (Gráficos)</label>
                            <select 
                                className="form-input" 
                                value={filters.year} 
                                onChange={e => handleFilterChange('year', parseInt(e.target.value))}
                            >
                                {yearOptions.map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>
                        
                        <div className="form-group">
                            <label>Mês (Gráfico Diário)</label>
                            <select 
                                className="form-input" 
                                value={filters.month} 
                                onChange={e => handleFilterChange('month', parseInt(e.target.value))}
                            >
                                {monthOptions.map(month => (
                                    <option key={month.value} value={month.value}>{month.label}</option>
                                ))}
                            </select>
                        </div>
                        
                        <div className="form-group">
                            <label>Início (KPIs)</label>
                            <DatePicker 
                                selected={filters.startDate} 
                                onChange={date => handleFilterChange('startDate', date)} 
                                locale={datePickerLocale} 
                                dateFormat="dd/MM/yyyy" 
                                className="form-input" 
                            />
                        </div>
                        
                        <div className="form-group">
                            <label>Fim (KPIs)</label>
                            <DatePicker 
                                selected={filters.endDate} 
                                onChange={date => handleFilterChange('endDate', date)} 
                                locale={datePickerLocale} 
                                dateFormat="dd/MM/yyyy" 
                                className="form-input" 
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* 🔥 CARDS DE KPIs */}
            <div className="dashboard-grid" style={{ marginTop: '1.5rem' }}>
                <KpiCard
                    icon={DollarSign}
                    title="Faturamento (Período)"
                    value={formatCurrency(dashboardData?.kpis?.faturamentoTotal, contaCurrency, contaLocale)}
                    isLoading={isLoading}
                />
                <KpiCard
                    icon={TrendingUp}
                    title="Ticket Médio (Período)"
                    value={formatCurrency(dashboardData?.kpis?.ticketMedio, contaCurrency, contaLocale)}
                    isLoading={isLoading}
                />
                <KpiCard 
                    icon={BarChart2} 
                    title="Agendamentos (Período)" 
                    value={dashboardData?.kpis?.totalAgendamentos || 0} 
                    isLoading={isLoading} 
                />
                <KpiCard 
                    icon={Users} 
                    title="Novos Clientes (Período)" 
                    value={dashboardData?.kpis?.novosClientes || 0} 
                    isLoading={isLoading} 
                />
            </div>
            
            {/* 🔥 GRÁFICOS */}
            <div className="row" style={{ marginTop: '1.5rem' }}>
                <div className="col-lg-12 mb-4">
                    <div className="chart-container">
                        <BarChart 
                            title={`Evolução Diária de Agendamentos`} 
                            data={dailyChartData} 
                            options={chartOptions} 
                        />
                    </div>
                </div>
                
                <div className="col-lg-12 mb-4">
                    <div className="chart-container">
                        <BarChart 
                            title={`Evolução Mensal de Agendamentos`} 
                            data={monthlyChartData} 
                            options={chartOptions} 
                        />
                    </div>
                </div>
                
                <div className="col-lg-12 mb-4">
                    <div className="chart-container">
                        <BarChart 
                            title="Evolução Anual (Últimos 5 Anos)" 
                            data={annualChartData} 
                            options={chartOptions} 
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContaDashboard;